// Phase 12 — star/unstar button. Reads & writes through the library store
// so every instance on the page stays in sync via useSyncExternalStore.

import { isFavorite, toggleFavorite, useLibrary } from "@/lib/library";

interface Props {
  symbol: string;
  variant?: "icon" | "chip";
}

export function FavoriteButton({ symbol, variant = "chip" }: Props) {
  // Subscribe — re-render when favorites change. Read via isFavorite for the
  // current snapshot (cheaper than scanning the array twice).
  useLibrary();
  const fav = isFavorite(symbol);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(symbol);
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={fav}
        aria-label={fav ? `Unfavorite ${symbol}` : `Favorite ${symbol}`}
        title={fav ? "Remove from favorites" : "Save to favorites"}
        className={
          "inline-flex h-8 w-8 items-center justify-center rounded-md border text-base transition " +
          (fav
            ? "border-accent bg-accent/15 text-accent"
            : "border-border bg-surface/60 text-muted-foreground hover:bg-secondary hover:text-foreground")
        }
      >
        <span aria-hidden="true">{fav ? "★" : "☆"}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={fav}
      aria-label={fav ? `Unfavorite ${symbol}` : `Favorite ${symbol}`}
      className={
        "inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors " +
        (fav
          ? "border-accent bg-accent/15 text-accent hover:bg-accent/20"
          : "border-border bg-surface/60 text-foreground hover:bg-secondary")
      }
    >
      <span aria-hidden="true" className="mr-1.5">{fav ? "★" : "☆"}</span>
      {fav ? "Favorited" : "Favorite"}
    </button>
  );
}
